import { config } from "@/config";
import { axiosExceptionToHttpError } from "@/utils/errorHandling";
import { resourceUrl } from "@/utils/stringUtils";
import axios, { AxiosResponse } from "axios";
import { HttpError } from "../core/HttpError";
import StoreModule from "../core/StoreModule";
import { $layoutSource } from "../layout_source/store";
import { $storageObject } from "../storage_object/store";
import { $wizardInstace } from "../wizard_instance/store";
import { LayoutState } from "./state";
import {
  CreatingLayout,
  EditingLayout,
  LayoutGetResponse,
  LayoutInsert,
  LayoutInsertType,
  LAYOUTINSERT_DEFAULT_HEIGHT,
  LAYOUTINSERT_DEFAULT_WIDTH,
  LayoutPostPayload,
  RESOURCE_PATH,
} from "./types";

class LayoutStore extends StoreModule<LayoutState> {
  protected state: LayoutState = {
    layout: {
      name: "",
      inserts: [],
    },
  };

  get layout() {
    return this.state.layout;
  }

  set layout(val: LayoutState["layout"]) {
    this.setState({ layout: val });
  }

  get layoutName() {
    return this.layout.name;
  }

  set layoutName(val) {
    this.setState({
      ...this.state,
      layout: { ...this.state.layout, name: val },
    });
  }

  getInsert(index: number) {
    return this.layout?.inserts[index] as LayoutInsert;
  }

  addInsert(
    page: number,
    type: LayoutInsert["type"] = LayoutInsertType.TEXT
  ): [number, LayoutInsert] {
    if (this.state.layout) {
      this.setState({
        ...this.state,
        layout: {
          ...this.state.layout,
          inserts: [
            ...this.state.layout.inserts,
            {
              ref: (Math.random() + 1).toString(36).substring(2),
              type: type,
              page: page,
              lly:
                $layoutSource.getPageMetadata(0).height -
                  LAYOUTINSERT_DEFAULT_HEIGHT || 0,
              llx: 0,
              width: LAYOUTINSERT_DEFAULT_WIDTH,
              height: LAYOUTINSERT_DEFAULT_HEIGHT,
              textContent:
                type !== LayoutInsertType.IMAGE.toString()
                  ? {
                      content: "",
                      fontSize: 12,
                      alignment: "left",
                    }
                  : undefined,
              imageContent:
                type !== LayoutInsertType.TEXT.toString()
                  ? {
                      content: "",
                      source: "embedded",
                    }
                  : undefined,
            },
          ],
        },
      });
    }
    const idx = this.state.layout.inserts.length - 1;
    return [idx, this.state.layout.inserts[idx]];
  }

  updateInsert(idx: number, val: Partial<LayoutInsert>) {
    this.setState({
      ...this.state,
      layout: {
        ...this.state.layout,
        inserts: this.state.layout.inserts.map((insert, index) => {
          if (index == idx) {
            return { ...insert, ...val };
          } else {
            return insert;
          }
        }),
      },
    });
  }

  removeInsert(index: number) {
    this.layout.inserts.splice(index, 1);
  }

  /**
   * Returns a persited layout with given ID.
   *
   * @param id
   */
  async fetch(id: string): Promise<[EditingLayout | null, Error | null]> {
    try {
      // Fetch layout from API
      const resp = await axios.get<LayoutGetResponse>(
        resourceUrl(config.api.BASE_URL, RESOURCE_PATH, id)
      );
      // Set storage object
      $storageObject.layoutSourceObject = resp.data.source.storageObject;
      // Set layout source
      $layoutSource.layoutSource = resp.data.source;
      // Set layout
      this.state.layout = {
        id: resp.data.id,
        inserts: resp.data.inserts,
        name: resp.data.name,
        createdAt: resp.data.createdAt,
        updatedAt: resp.data.updatedAt,
      };
      return [this.layout as EditingLayout, null];
    } catch (err: any) {
      console.log(err);
      if (err.name && err.name == "AxiosError") {
        return [null, axiosExceptionToHttpError(err)];
      }
      return [null, new HttpError(err.message, 500, err.stack)];
    }
  }
  /**
   * Creates a new empty layout object and sets the state accordingly.
   */
  create(): CreatingLayout {
    this.layout = {
      name: "",
      inserts: [],
    };
    return this.layout;
  }
  /**
   * Saves a newly created layout or updates an existing one.
   */
  async save(
    layout?: LayoutPostPayload
  ): Promise<[LayoutGetResponse | null, HttpError | null]> {
    try {
      const resp = await axios.post<
        any,
        AxiosResponse<LayoutGetResponse>,
        LayoutPostPayload
      >(
        resourceUrl(config.api.BASE_URL, RESOURCE_PATH),
        layout || {
          layoutSourceId: $layoutSource.layoutSource?.id as string,
          wizardInstanceId: $wizardInstace.wizardInstance?.id as string,
          name: this.layout?.name as string,
          inserts: this.layout?.inserts as LayoutInsert[],
        }
      );
      return [resp.data, null];
    } catch (err: any) {
      console.log(err);
      if (err.name && err.name == "AxiosError") {
        return [null, axiosExceptionToHttpError(err)];
      }
      return [null, new HttpError(err.message, 500, err.stack)];
    }
  }
}

export const $layout = StoreModule.singleton(LayoutStore);
